import { Remove } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import * as Yup from 'yup';
import { ICareTeam } from '../../../Models/CareTeams/CareTeam.model';
import { ITeamRole } from '../../../Models/CareTeams/TeamRole.model';
import { Provider } from '../../../Models/Provider/Provider.model';
import IProviderSelectOption from '../../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../../Models/Role/Role.model';
import { RootState } from '../../../reducers';
import { addTeam, closeAddCareTeamModal } from '../../../store/careTeams.slice';
import { apiString } from '../../../utils/constants';
import TeamAddressForm, { AddressValidationSchema } from '../TeamAddressForm';

const TeamRoleSchema = Yup.object().shape({
    RoleId: Yup.string().required('Role is required'),
    AssignedProviderId: Yup.string().required('Assigned Provider is required'),
});

const CareTeamSchema = Yup.object().shape({
    Name: Yup.string().required('Team Name is required'),
    MeetingAddress: AddressValidationSchema,
    MaxTeamSize: Yup.number().required('Max Team Size is required').min(1, 'Must be at least 1'),
    TeamRoles: Yup.array().of(TeamRoleSchema).min(1, '*At least one team role is required'),
});

const AddCareTeamModal: React.FC = () => {
    const [providerOptions, setProviderOptions] = useState<IProviderSelectOption[]>([]);
    const [allowedRoles, setAllowedRoles] = useState<IRole[]>([]);
    const [assignedProviderNames, setAssignedProviderNames] = useState<string[]>([]);
    const dispatch = useAppDispatch();
    const { isAddCareTeamModalOpen } = useSelector((state: RootState) => state.careTeams);
    const closeModal = () => {
        setAssignedProviderNames([]);
        dispatch(closeAddCareTeamModal());
    };
    const handleSubmit = (values: ICareTeam, { resetForm }: { resetForm: () => void }) => {
        dispatch(addTeam(values));
        resetForm();
        closeModal();
    };
    const initialValues: ICareTeam = {
        Id: '',
        Name: '',
        MeetingAddress: {
            Address1: '',
            Address2: '',
            City: '',
            State: '',
            ZipCode: '',
            FullAddress: '',
            Country: '',
            Lat: 0,
            Lon: 0,
        },
        TeamRoles: [],
        MaxTeamSize: 1,
        AssignedMemberIds: [],
        TeamRoleAlternates: [],
        IsDeleted: false
    };

    const checkAssignedProviders = async (careTeam: ICareTeam) => {
        try {
            const response = await axios.post(`${apiString}/CareTeams/GetProvidersAssignedToOtherTeams`, careTeam);
            if (response.status === 200) {
                setAssignedProviderNames(response.data);
            }
        } catch (error) {
            console.error('Error fetching assigned providers:', error);
        }
    };

    useEffect(() => {
        axios.get(`${apiString}/Provider/getactiveproviders`).then((response) => {
            if (response.status === 200) {
                const responseOptions = response.data.map((provider: Provider) => ({
                    ProviderId: provider.Id,
                    ProviderName: `${provider.LastName}, ${provider.FirstName}`,
                    RoleName: provider.Role.RoleName,
                    RoleId: provider.Role.Id,
                }));
                setProviderOptions(responseOptions);
            }
        });

        axios.get(`${apiString}/role/roles`).then((response) => {
            if (response.status === 200) {
                setAllowedRoles(response.data.filter((r: { IsCareTeamRole: boolean }) => r.IsCareTeamRole));
            }
        });
    }, []);

    return (
        <Formik initialValues={initialValues} validationSchema={CareTeamSchema} onSubmit={handleSubmit}>
            {({ values, errors, handleChange, handleBlur, touched, submitForm, setFieldValue, resetForm }: FormikProps<ICareTeam>) => (
                <Dialog
                    open={isAddCareTeamModalOpen}
                    onClose={closeModal}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        sx: {
                            overflow: 'visible',
                            zIndex: 1300,
                        },
                    }}
                >
                    <DialogTitle>Add Care Team</DialogTitle>
                    <DialogContent>
                        <Form>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 8, lg: 10 }}>
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        label="Team Name"
                                        name="Name"
                                        value={values.Name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.Name && Boolean(errors.Name)}
                                        helperText={touched.Name && errors.Name}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4, lg: 2 }}>
                                    <TextField
                                        sx={{ marginTop: 2 }}
                                        fullWidth
                                        label="Max Team Size"
                                        name="MaxTeamSize"
                                        type="number"
                                        value={values.MaxTeamSize}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.MaxTeamSize && Boolean(errors.MaxTeamSize)}
                                        helperText={touched.MaxTeamSize && errors.MaxTeamSize}
                                    />
                                </Grid>
                                <TeamAddressForm />
                                <Grid size={12}>
                                    <Paper variant="outlined" sx={{ padding: 2, position: 'relative' }}>
                                        <Box sx={{ position: 'absolute', top: -12, left: 16, backgroundColor: 'white', padding: '0 8px' }}>
                                            <Typography variant="subtitle1">Team Roster</Typography>
                                        </Box>
                                        {assignedProviderNames.length > 0 && (
                                            <Alert variant="outlined" sx={{ p: 1, my: 2 }} severity="info">
                                                The following providers would have multiple team assignments: {assignedProviderNames.join(', ')}
                                            </Alert>
                                        )}
                                        {allowedRoles.length === 0 ? (
                                            <Alert variant="outlined" sx={{ p: 1, my: 2 }} severity="info">
                                                There are no allowed roles. Please add and save them on the admin roles screen.
                                            </Alert>
                                        ) : (
                                            <FieldArray name="TeamRoles">
                                                {({ push, remove }) => (
                                                    <>
                                                        {values.TeamRoles.map((role, index) => {
                                                            const availableProviders = providerOptions.filter((provider) => provider.RoleId === role.RoleId);
                                                            return (
                                                                <Grid container spacing={2} key={index} sx={{ marginBottom: 2 }}>
                                                                    <Grid size={12}>
                                                                        <Grid container spacing={2}>
                                                                            <Grid size={5}>
                                                                                <Autocomplete
                                                                                    options={allowedRoles.filter(
                                                                                        (allowedRole) =>
                                                                                            !values.TeamRoles.some(
                                                                                                (teamRole) => teamRole.RoleId === allowedRole.Id
                                                                                            ) || allowedRole.Id === role.RoleId
                                                                                    )}
                                                                                    getOptionLabel={(option) => option.Name}
                                                                                    onChange={(event, value) =>
                                                                                        setFieldValue(`TeamRoles.${index}.RoleId`, value?.Id || '')
                                                                                    }
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            label="Role"
                                                                                            name={`TeamRoles.${index}.RoleId`}
                                                                                            value={role.RoleId}
                                                                                            onChange={handleChange}
                                                                                            onBlur={handleBlur}
                                                                                            error={
                                                                                                touched.TeamRoles?.[index]?.RoleId &&
                                                                                                Boolean((errors.TeamRoles?.[index] as ITeamRole)?.RoleId)
                                                                                            }
                                                                                            helperText={
                                                                                                touched.TeamRoles?.[index]?.RoleId &&
                                                                                                (errors.TeamRoles?.[index] as ITeamRole)?.RoleId
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                />
                                                                            </Grid>
                                                                            <Grid size={5}>
                                                                                <Autocomplete
                                                                                    options={availableProviders}
                                                                                    getOptionLabel={(option) => option.ProviderName}
                                                                                    onChange={(event, value) => {
                                                                                        setFieldValue(
                                                                                            `TeamRoles.${index}.AssignedProviderId`,
                                                                                            value?.ProviderId || ''
                                                                                        );
                                                                                        setFieldValue(
                                                                                            `TeamRoles.${index}.ProviderName`,
                                                                                            value?.ProviderName || ''
                                                                                        );
                                                                                        checkAssignedProviders({
                                                                                            ...values,
                                                                                            TeamRoles: [
                                                                                                ...values.TeamRoles.slice(0, index),
                                                                                                {
                                                                                                    ...values.TeamRoles[index],
                                                                                                    AssignedProviderId: value?.ProviderId || '',
                                                                                                },
                                                                                                ...values.TeamRoles.slice(index + 1),
                                                                                            ],
                                                                                        });
                                                                                    }}
                                                                                    disabled={!role.RoleId}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            label="Assigned Provider"
                                                                                            name={`TeamRoles.${index}.AssignedProviderId`}
                                                                                            value={role.AssignedProviderId}
                                                                                            onChange={handleChange}
                                                                                            onBlur={handleBlur}
                                                                                            error={
                                                                                                touched.TeamRoles?.[index]?.AssignedProviderId &&
                                                                                                Boolean(
                                                                                                    (errors.TeamRoles?.[index] as ITeamRole)?.AssignedProviderId
                                                                                                )
                                                                                            }
                                                                                            helperText={
                                                                                                touched.TeamRoles?.[index]?.AssignedProviderId &&
                                                                                                (errors.TeamRoles?.[index] as ITeamRole)?.AssignedProviderId
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                />
                                                                            </Grid>
                                                                            <Grid size={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <IconButton onClick={() => remove(index)}>
                                                                                    <Remove />
                                                                                </IconButton>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            );
                                                        })}
                                                        {errors.TeamRoles && typeof errors.TeamRoles === 'string' && (
                                                            <Grid size={12}>
                                                                <Typography color="error">{errors.TeamRoles}</Typography>
                                                            </Grid>
                                                        )}
                                                        <Grid size={12}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={() => push({ RoleId: '', AssignedProviderId: '', AlternateProviderPerMemberMap: {} })}
                                                            >
                                                                Add Role
                                                            </Button>
                                                        </Grid>
                                                    </>
                                                )}
                                            </FieldArray>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Form>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Inline Formik errors, now right next to the buttons */}
                            {(typeof errors === 'string' && errors) ||
                                (errors && typeof errors === 'object' && Object.keys(errors).length > 0 && (
                                    <Typography color="error" variant="body2" sx={{ fontSize: 13, mr: 2 }}>
                                        {Object.entries(errors)
                                            .map(([key, value]) => (typeof value === 'string' ? value : null))
                                            .filter(Boolean)
                                            .join(' | ')}
                                    </Typography>
                                ))}
                            <Button
                                onClick={() => {
                                    resetForm();
                                    closeModal();
                                }}
                                variant="contained"
                                color="secondary"
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                form="careTeamForm"
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    console.log('Submitting form...');
                                    submitForm();
                                }}
                            >
                                Save { }
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            )}
        </Formik>
    );
};

export default AddCareTeamModal;
